/**
 * utils/salespeople.js
 * Pure functions for managing the salespeople collection (Phase T1 transaction ledger;
 * extended for the Person/Role restructure — Stage 1 was data layer only, Stage 2 moved every
 * display read onto the person layer and removed the role's own frozen name entirely; Stage 4
 * moved `code` onto the person the same way; Stage 5 pinned a role to its location for life).
 *
 * `data.salespeople`: { id, personId, branchId, commissionRate, active, createdAt }[].
 * `active` (Stage 3 batch 1, default `true`) exists so removing someone from a branch can be
 * REVERSIBLE — deactivating a role, never deleting it, is what lets Stage 5's own move action
 * put someone back at a branch they'd left and have their past transactions reconnect (the
 * role id survives).
 *
 * **Stage 5 — a role is pinned to its branchId for life, with one narrow exception.** A role's
 * transactions are frozen at creation to the branch they actually happened at; if the role
 * itself could later move, its own lifetime transaction COUNT would read as a count of
 * wherever it sits TODAY, which is not what those transactions are actually about. So:
 * `updateSalesperson`'s own `branchId` handling REFUSES the change outright once
 * `salespersonRefCount(data, id) > 0` — a 0-transaction role is the one exception, still
 * directly correctable (there's nothing to misattribute). Relocating a role WITH transactions
 * instead goes through `moveSalespersonBranch` (below): deactivate the current role, pick up
 * (or reactivate) a role at the destination — one atomic action, not a raw field edit.
 * **One role per person per location, ever (Part B)** — a person can hold at most one role,
 * active or inactive, at a given branchId, enforced inside `addSalesperson` itself (below): an
 * ACTIVE role already there REFUSES the add; an INACTIVE one is REACTIVATED in place rather
 * than a second row being created.
 * **This collection is now a ROLE collection in MEANING, not in name.** A record describes one
 * person selling in one place — Pusat (`branchId: null`) or a specific Cabang branch
 * (`branchId` a real `data.branches` id). One person may hold several roles (a Pusat role plus
 * one or more Cabang roles), each sharing that person's id via `personId` — see utils/persons.js.
 * The collection keeps its old name deliberately: every transaction, guard, picker, and test in
 * this app refers to it by `salespeople`/`salespersonId`, and renaming it would churn hundreds
 * of call sites for no functional gain.
 *
 * **A role has no code of its own — this is now load-bearing, not just documented (Stage 4).**
 * The code identifies the PERSON, not where they happen to be selling: Budi is BU wherever he
 * works — TMM, MMC, Pusat. A code is always the linked person's CURRENT code, resolved via
 * `resolveSalespersonCode` below — the ONE thing a minted cabang invoice number's counter is
 * keyed on (`mintCabangNumber`), so a person's several roles always share one counter, and two
 * DIFFERENT people can never share a code (uniqueness lives in `persons.js` now, enforced
 * globally across every person, not just within this collection). `addSalesperson`/
 * `updateSalesperson` below never write a `code` field onto a role at all — `updateSalesperson`'s
 * handling of a supplied `fields.code` recodes the LINKED PERSON (via `updatePerson`), the same
 * delegation `fields.name` already uses (see below), which is what keeps `KelolaSalesModal`'s
 * existing "Ubah Nama" edit row (which has always also sent a `code`) honestly functional now
 * that the role has nowhere of its own to store one. `commissionRate` is
 * a plain per-role number (default 0, not used by anything yet — storage only).
 *
 * **A role has no name of its own — this is now load-bearing, not just documented.** A name is
 * always the linked person's CURRENT name, resolved via `resolveSalespersonName` below. Stage 1
 * kept a frozen `name` snapshot on every role so existing UI reads (`sp.name`) kept working
 * un-migrated; Stage 2 moved every one of those reads onto `resolveSalespersonName`/
 * `displaySalesperson` and deleted the field — the v21→v22 storage migration strips it from
 * existing records (see storage.js). `addSalesperson`/`updateSalesperson` below never write it.
 * `updateSalesperson`'s handling of a supplied `fields.name` now RENAMES THE LINKED PERSON
 * (via `updatePerson`) instead of stamping a frozen copy onto the role — this is what keeps
 * `KelolaSalesModal`'s existing "Ubah Nama" control working at all now that the role has
 * nowhere of its own to store a name; see that function's own comment below.
 *
 * `branchId` is now OPTIONAL — `null` is a deliberate, valid value meaning "this role sells at
 * Pusat," not a hole to defend against. A non-null value must still resolve against
 * `data.branches`. `personId` is required on every stored role and the CALLER must supply one
 * that already resolves against `data.persons` — Stage 1's auto-mint-on-missing-`personId`
 * bridge (which silently minted a brand-new Person whenever a caller omitted one) is REMOVED as
 * of Stage 3 batch 2. A caller with no person to link yet must create one first via
 * `persons.js`'s `addPerson` — Stage 3 batch 3a is what actually did this: `App.js`'s
 * `createSalespersonRole` calls `addPerson`/`addSalesperson` together, atomically, and both
 * `KelolaSalesModal`'s add flow and the transaction form's quick-add now go through a shared
 * `SalespersonFormModal` that asks WHO before it asks WHERE (see that component's own header
 * comment for the full two-stage design).
 *
 * **Deactivation (Stage 3 batch 2)**: removing someone from a branch must be REVERSIBLE — their
 * past transactions must keep resolving, and putting them back must reconnect everything. So
 * "remove from a branch" means flipping `active` to `false` (`deactivateSalesperson` below),
 * never deleting the role. `deleteSalesperson` stays a PERMANENT delete for the one case that
 * genuinely has nothing to preserve: a role created by mistake and never used (guarded by
 * `salespersonRefCount === 0`, unaffected by `active` either way — see that function's own
 * comment). See `deactivateSalesperson`/`reactivateSalesperson` below.
 */
import { generateId } from "./idGenerators";
import { personExists, updatePerson, resolvePersonName, resolvePersonNote, resolvePersonCode } from "./persons";
import { resolveBranchName } from "./branches";

function norm(s) {
  return String(s || "").trim();
}
function branchExists(branches, id) {
  return (branches || []).some((b) => b.id === id);
}
function hasKey(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}
/** Validates a commissionRate: undefined/null -> default 0 (valid); a real finite number >= 0
 *  -> that value (valid); anything else -> NaN, a sentinel the caller checks for and rejects
 *  the whole write on, matching this file's existing all-or-nothing validation convention. */
function normCommissionRate(v) {
  if (v == null) return 0;
  return (typeof v === "number" && Number.isFinite(v) && v >= 0) ? v : NaN;
}

/**
 * Add a new salesperson (role). Same-reference no-op when `fields.branchId` is absent entirely
 * (a Pusat-vs-Cabang decision is required — pass `null` explicitly for Pusat) or non-null but
 * doesn't resolve against `data.branches`, `fields.commissionRate` is given but not a real
 * finite number >= 0, `fields.active` is given but not a real boolean, or `fields.personId` is
 * missing or doesn't resolve against `data.persons`.
 *
 * `personId` is a HARD REQUIREMENT with no fallback — Stage 1's auto-mint bridge is gone (see
 * this file's own header comment for why, and what it means for existing UI callers). The
 * caller must already have a real person to link; there is no `fields.name`/`fields.code`
 * handling here at all — a role has no name or code of its own regardless (Stage 2/Stage 4), and
 * minting a person (with their own permanent code) from a typed name is squarely a caller-side
 * decision now (persons.js's own `addPerson`), not something this function does on a caller's
 * behalf.
 */
export function addSalesperson(data, fields) {
  const list = data.salespeople || [];

  if (!hasKey(fields, "branchId")) return data;
  const branchId = fields.branchId;
  if (branchId !== null && !branchExists(data.branches, branchId)) return data;

  const commissionRate = normCommissionRate(fields?.commissionRate);
  if (Number.isNaN(commissionRate)) return data;

  const active = hasKey(fields, "active") ? fields.active : true;
  if (typeof active !== "boolean") return data;

  const personId = fields?.personId;
  if (personId == null || !personExists(data.persons, personId)) return data;

  // Stage 5 (one role per person per location, ever — see this file's own header comment):
  // a person can hold at most ONE role at a given branchId, active or inactive, for their
  // whole life. An ACTIVE role already there is REFUSED outright — a same-reference no-op,
  // the identical "invalid, not ambiguous" treatment a bad branchId or a colliding code
  // already get, never an advisory dialog. An INACTIVE role already there is REACTIVATED in
  // place instead of a second row being created — this is what reconnects a returning
  // person's own old transaction history at that same location, rather than starting them
  // fresh at zero for no reason. `commissionRate`/`active` supplied by THIS call are ignored
  // on the reactivate path — everything about the existing record except `active` stays
  // exactly as it was, the same "reactivating puts someone back exactly where they were, not
  // somewhere new" contract `reactivateSalesperson` already promises.
  const existingIdx = list.findIndex((s) => s.personId === personId && s.branchId === branchId);
  if (existingIdx !== -1) {
    const existing = list[existingIdx];
    if (existing.active !== false) return data; // already active there — refused, not ambiguous
    const reactivated = { ...existing, active: true };
    return { ...data, salespeople: list.map((s, i) => (i === existingIdx ? reactivated : s)) };
  }

  const sp = { id: generateId(), branchId, personId, commissionRate, active, createdAt: new Date().toISOString() };
  return { ...data, salespeople: [...list, sp] };
}

/**
 * Update an existing salesperson (role)'s branch/commissionRate/personId — and, when a `name`
 * and/or `code` is supplied, the LINKED PERSON's own name/code (the role itself stores neither).
 * Same-reference no-op when `id` doesn't resolve, a supplied `branchId` is non-null but doesn't
 * resolve, a supplied `commissionRate` isn't a real finite number >= 0, a supplied `personId`
 * doesn't resolve against `data.persons`, or a supplied `name`/`code` is rejected by
 * `persons.js`'s own `updatePerson` (blank name; a code failing the 1-4-char format or
 * colliding with ANOTHER person's — renaming to the person's own current code is fine).
 *
 * `branchId`/`personId` use explicit-key-presence (not truthiness) so a caller can deliberately
 * set `branchId: null` to move a role TO Pusat — omitting the key keeps the role's current
 * branch, exactly like every other field here. This is the fix for a role that previously could
 * never be moved to Pusat: the old code treated an explicit `null` the same as "not supplied."
 * **Moving `branchId` on an already-created role (Stage 3 batch 2, Part D) is a plain,
 * unconditional correction here** — this function never checks `salespersonRefCount` before
 * allowing it; see that function's own comment for why a nonzero count is a WARNING a later
 * batch's UI surfaces, never a block enforced here.
 *
 * `fields.name`/`fields.code` — kept for backward compatibility with every existing caller
 * (`KelolaSalesModal`'s "Ubah Nama" edit row has always sent both): since Stage 2 removed the
 * role's own frozen `name` field and Stage 4 removed its `code` field the same way, either
 * supplied value now edits the role's LINKED PERSON via ONE `updatePerson` call (both fields
 * merged into a single write, so a combined name+code edit either both apply or neither does)
 * instead of stamping a copy onto the role. This is a deliberate, considered choice, not an
 * incidental side effect — leaving them silent no-ops here would make "Ubah Nama" appear to work
 * (no error, modal closes) while doing nothing visible, since every display now resolves through
 * the person. `updatePerson`'s own rejection (a same-reference no-op there) propagates as THIS
 * function's own same-reference no-op — an invalid/colliding code typed into a role's edit row
 * must reject the WHOLE update, not silently save the branch/active change while dropping the
 * code. A role currently sharing its person with a sibling role (e.g. a Pusat role plus a Cabang
 * role for the same Budi) would recode/rename BOTH roles at once, by design — that's the entire
 * point of Stage 4: the code follows the person, not the role.
 *
 * `fields.active` (Stage 3 batch 1 added the field; batch 2 added the two named wrappers below):
 * omitted -> preserves the role's current value, exactly like every other field here; supplied
 * -> must be a real boolean, else same-reference no-op. `deactivateSalesperson`/
 * `reactivateSalesperson` (below) are the intended call sites — this function stays the single
 * underlying write path for both directions of the toggle, nothing else changes to support it.
 */
export function updateSalesperson(data, id, fields) {
  const list = data.salespeople || [];
  const idx = list.findIndex((s) => s.id === id);
  if (idx === -1) return data;

  const current = list[idx];

  const branchId = hasKey(fields, "branchId") ? fields.branchId : current.branchId;
  if (branchId !== null && !branchExists(data.branches, branchId)) return data;

  // Stage 5 (roles pinned to their location — see this file's own header comment): a role's
  // branchId can only be corrected directly while it carries NO transactions at all — once it
  // does, a direct branchId edit here is REFUSED (same-reference no-op); the only way to
  // relocate someone with real history is `moveSalespersonBranch` below, which deactivates
  // this role and picks up (or reactivates) a role at the destination instead. And even a
  // 0-transaction role can never be walked directly onto a branch the SAME person already
  // holds another role at, active or inactive — a plain field correction has no way to merge
  // two rows into one the way `addSalesperson`'s own reactivate-on-add path (above) does, so
  // landing there would leave two separate records for one person at one place, exactly what
  // Part B's one-role-per-person-per-location rule forbids.
  if (branchId !== current.branchId) {
    if (salespersonRefCount(data, id) > 0) return data;
    const collides = list.some((s) => s.id !== id && s.personId === current.personId && s.branchId === branchId);
    if (collides) return data;
  }

  const commissionRate = fields?.commissionRate !== undefined
    ? normCommissionRate(fields.commissionRate)
    : current.commissionRate;
  if (Number.isNaN(commissionRate)) return data;

  const active = hasKey(fields, "active") ? fields.active : current.active;
  if (typeof active !== "boolean") return data;

  const personId = hasKey(fields, "personId") ? fields.personId : current.personId;
  if (personId == null || !personExists(data.persons, personId)) return data;

  let persons = data.persons;
  if (fields?.name != null || fields?.code != null) {
    const personFields = {};
    if (fields?.name != null) personFields.name = fields.name;
    if (fields?.code != null) personFields.code = fields.code;
    const updated = updatePerson(data, personId, personFields);
    if (updated === data) return data; // invalid/colliding name or code -- reject the whole write
    persons = updated.persons;
  }

  const next = { ...current, branchId, commissionRate, active, personId };
  return { ...data, persons, salespeople: list.map((s, i) => (i === idx ? next : s)) };
}

/**
 * Deactivate a role (Stage 3 batch 2) — sets `active: false`. ALWAYS allowed, regardless of
 * whether any transaction (open or otherwise) currently references it — this is the deliberate
 * difference from `deleteSalesperson` below: deactivation never checks `salespersonRefCount` at
 * all. The record survives with its id untouched, so every past transaction referencing it keeps
 * resolving through `resolveSalespersonName`/`displaySalesperson` exactly as before; this is the
 * whole mechanism that makes "remove from a branch" reversible instead of a permanent delete. A
 * thin, named wrapper over `updateSalesperson` — same-reference no-op only when `id` doesn't
 * resolve (inherited from `updateSalesperson`; every other field is left at its current,
 * already-valid value, so nothing else here can fail). Idempotent: deactivating an
 * already-inactive role is a harmless no-change write.
 */
export function deactivateSalesperson(data, id) {
  return updateSalesperson(data, id, { active: false });
}

/**
 * Reactivate a role (Stage 3 batch 2) — sets `active: true`, nothing else changes. Symmetric
 * with `deactivateSalesperson` above. A role's `branchId`/`code`/`personId`/`commissionRate` are
 * never disturbed by either direction of this toggle — reactivating puts someone back exactly
 * where they were, not somewhere new.
 */
export function reactivateSalesperson(data, id) {
  return updateSalesperson(data, id, { active: true });
}

/**
 * The ONE action for relocating a role that has real transaction history (Stage 5 — roles
 * pinned to their location; see this file's own header comment). Splitting this into two
 * separate steps (deactivate, then separately add a role) would be friction without benefit,
 * since the user would always do both together — this is one atomic write: deactivate the
 * CURRENT role (`updateSalesperson`, `{active:false}` — never touches its own `branchId`, so
 * its existing transactions keep resolving through it exactly as before), then create-or-
 * reactivate a role at `newBranchId` for the SAME person via `addSalesperson`'s own Part-B
 * reactivate-or-refuse logic (above) — a destination the person already holds an INACTIVE role
 * at is reactivated (reconnecting whatever transaction history it already has there); a
 * destination they already hold an ACTIVE role at REFUSES the whole move (same-reference
 * no-op, the original `data` untouched — the deactivate half never actually commits) rather
 * than leaving the person mid-move with neither a working old role nor a new one.
 *
 * Same-reference no-op when `id` doesn't resolve, `newBranchId` is non-null but doesn't resolve
 * against `data.branches`, or `newBranchId` equals the role's own CURRENT branch (nothing to
 * move). Unlike a plain `updateSalesperson({branchId})` correction, this works regardless of
 * whether the role has any transactions — a 0-transaction role CAN be moved this way too — but
 * the UI only ever offers it once a role has real history (a 0-transaction role's own branchId
 * is still just directly correctable, see `updateSalesperson` above); nothing here forces that
 * restriction at the data layer, since there's no invariant reason to.
 */
export function moveSalespersonBranch(data, id, newBranchId) {
  const list = data.salespeople || [];
  const current = list.find((s) => s.id === id);
  if (!current) return data;
  if (newBranchId !== null && !branchExists(data.branches, newBranchId)) return data;
  if (newBranchId === current.branchId) return data;

  const deactivated = updateSalesperson(data, id, { active: false });
  if (deactivated === data) return data; // defensive — id already resolved above

  const moved = addSalesperson(deactivated, {
    personId: current.personId, branchId: newBranchId, commissionRate: current.commissionRate,
  });
  if (moved === deactivated) return data; // destination already ACTIVE for this person — whole move refused

  return moved;
}

/** Number of transactions referencing this salesperson (txn.salespersonId). Three distinct
 *  consumers read this SAME count, not independent ones: `deleteSalesperson` below (a role with
 *  any transaction can never be permanently deleted), `updateSalesperson`'s own `branchId`
 *  guard above (Stage 5 — a role with any transaction can never have its branchId corrected
 *  directly; `moveSalespersonBranch` is the only way to relocate it), and `KelolaSalesModal`'s
 *  own UI (which of the two branch-editing mechanisms — the plain edit row, or the "Pindahkan"
 *  move action — to offer for a given role). This function itself never blocks anything on its
 *  own beyond what `updateSalesperson`/`deleteSalesperson` already enforce. */
export function salespersonRefCount(data, id) {
  return (data.transactions || []).filter((t) => t.salespersonId === id).length;
}

/**
 * Delete only when unreferenced (salespersonRefCount === 0). Same-reference no-op otherwise
 * — the UI is expected to explain why via salespersonRefCount before offering delete. This is
 * the PERMANENT-delete path — "created by mistake, never used" — for the one case that has
 * nothing worth preserving; a role that HAS been used should be deactivated instead (see
 * `deactivateSalesperson` above), never deleted. Unaffected by `active` either way: a role can
 * be deleted regardless of whether it's currently active or inactive, since the guard is about
 * transaction history, not current status. Deliberately does NOT touch the linked person —
 * deleting a role never deletes its person, and a person with zero remaining roles is still a
 * perfectly valid record (see persons.js's own personRefCount/deletePerson for that decision).
 */
export function deleteSalesperson(data, id) {
  if (salespersonRefCount(data, id) > 0) return data;
  const list = data.salespeople || [];
  const next = list.filter((s) => s.id !== id);
  if (next.length === list.length) return data;
  return { ...data, salespeople: next };
}

/** Display label for a salesperson: "name (code)" — used by SalespersonPicker's/
 *  KelolaSalesModal's option labels, mirroring contacts.js's displayContact. Resolves both the
 *  name AND (Stage 4) the code through the linked person (a role has neither of its own) —
 *  falls back to "—" only when the person is unresolvable (a dangling `personId`, which cannot
 *  occur through this module's own write paths — see `resolveSalespersonName`/
 *  `resolveSalespersonCode` below), so at least SOMETHING renders rather than the whole label
 *  collapsing to nothing.
 *
 *  Stage 3 batch 3b: two OPTIONAL suffixes, appended in this order, each rendering nothing when
 *  it doesn't apply — the note (`resolvePersonNote`, blank for almost everyone, so this must
 *  never clutter the common case) and, when the role itself is deactivated, a plain "· Nonaktif"
 *  marker. The marker matters here specifically because `TransactionFormModal` only ever passes
 *  an inactive role into this function for ONE case — an edit form's own already-selected role,
 *  kept visible/selectable so the transaction stays resolvable/editable (see that component's
 *  own `visibleSalespeople`) — so a label like "Budi (BD) · Nonaktif" is what tells the user WHY
 *  a role they can't newly pick elsewhere is still sitting in this field. */
export function displaySalesperson(persons, s) {
  const name = resolveSalespersonName(persons, s) || "—";
  const code = resolveSalespersonCode(persons, s) || "—";
  const note = resolvePersonNote(persons, s.personId);
  const inactive = s.active === false;
  return `${name} (${code})${note ? ` — ${note}` : ""}${inactive ? " · Nonaktif" : ""}`;
}

/**
 * Resolve a role's CURRENT display name through its linked person — the ONLY way to read a
 * role's name, since a role has no name of its own (Stage 2 removed the frozen `name` field
 * entirely). Returns `null` when `sp` is missing or its `personId` doesn't resolve against
 * `persons` — a dangling `personId` cannot occur through this module's own write paths
 * (`addSalesperson`/`updateSalesperson` both validate it), only via external data corruption.
 * The caller sees `null` and falls back to its own placeholder, same as every other "resolve an
 * id, might not resolve" helper in this codebase (`resolveBranchName`, `resolvePersonName`).
 */
export function resolveSalespersonName(persons, sp) {
  if (!sp) return null;
  return resolvePersonName(persons, sp.personId);
}

/**
 * Resolve a role's CURRENT code through its linked person (Stage 4) — the ONLY way to read a
 * role's code, since a role has no code of its own anymore. Same `null`-when-unresolvable
 * contract as `resolveSalespersonName` above. This is what `App.js`'s `createTransaction`/
 * `editTransaction` call instead of reading `sp.code` directly before minting a cabang invoice
 * number (`mintCabangNumber`) — the code that feeds the counter is always the PERSON's code, so
 * Budi mints under the same counter at TMM, MMC, and Pusat alike.
 */
export function resolveSalespersonCode(persons, sp) {
  if (!sp) return null;
  return resolvePersonCode(persons, sp.personId);
}

/**
 * Every branch this person currently holds an ACTIVE role at, as resolved display names in
 * role order — `"Pusat"` for `branchId: null`, `resolveBranchName(...)` (falling back to the
 * generic `"Cabang"` label, same as everywhere else in this app) for a real branch. Returns
 * `[]` when the person has no active role at all (every role deactivated, or none ever
 * created) — the caller decides its own empty-state wording, same "don't fabricate a fallback"
 * contract every other resolver in this codebase follows.
 *
 * Built for Stage 3 batch 3a's person-search picker (`SalespersonFormModal`) — the "Abun —
 * Pusat, TMM" row label that lets a human tell two same-named people apart is this array,
 * comma-joined. **Same active-only filtering rule `persons.js`'s `detectPersonCollision` uses
 * internally** (an inactive role never counts as "where they work," there or here) — this is a
 * deliberate, small duplication of that one-line filter, not a shared import: the two functions
 * return different shapes for different purposes (raw branchIds for a set comparison there,
 * resolved names for a human to read here), and `persons.js` deliberately has no dependency on
 * `branches.js` for name resolution. If the active-role rule itself ever changes, both call
 * sites need the same edit — there are only two, and they're both in this codebase's own
 * salespeople/persons layer, not spread further.
 */
function branchIdName(branches, branchId) {
  return branchId === null ? "Pusat" : (resolveBranchName(branches, branchId) || "Cabang");
}

export function activeRoleLocations(persons, salespeople, branches, personId) {
  return (salespeople || [])
    .filter((s) => s.personId === personId && s.active !== false)
    .map((s) => branchIdName(branches, s.branchId));
}

/**
 * Every branch (Pusat included) where PERSONID could VALIDLY hold a role right now, for two
 * different callers sharing one filter (Stage 5 — one role per person per location, ever):
 *  - `mode: "edit"` — `KelolaSalesModal`'s plain, direct branchId correction on a
 *    0-transaction role. Excludes ANY branch where the person already holds ANOTHER role,
 *    active or inactive — a plain field edit has no way to merge two role records into one, so
 *    landing on a branch that already has a role would leave two rows for one person at one
 *    place, exactly what the one-role-per-location rule forbids.
 *  - `mode: "move"` — the deactivate-and-create/reactivate action (`moveSalespersonBranch`,
 *    above). Excludes only branches with an ACTIVE other role — an INACTIVE one there is
 *    exactly what gets reactivated (merged into), not a second row, so it's offered, flagged
 *    `willReactivate: true`.
 * `excludeRoleId` is the role being edited/moved itself, so its OWN branch is never counted as
 * "another role" against itself — for "edit" this is what keeps "leave it where it is"
 * selectable; for "move" the caller additionally filters out the role's own current branch
 * (there's nothing to move TO by picking where it already is), since only that one call site
 * wants that extra exclusion.
 * Returns `{id, label, willReactivate}[]`, `id` `null` meaning Pusat.
 */
export function pickableBranchesForPerson(persons, salespeople, branches, personId, excludeRoleId, mode) {
  const candidates = [{ id: null, name: "Pusat" }, ...(branches || [])];
  return candidates
    .map((b) => {
      const existing = (salespeople || []).find((s) => s.personId === personId && s.branchId === b.id && s.id !== excludeRoleId);
      return { id: b.id, label: b.id === null ? "Pusat" : b.name, existing };
    })
    .filter(({ existing }) => !existing || (mode === "move" && existing.active === false))
    .map(({ id, label, existing }) => ({ id, label, willReactivate: !!existing }));
}

/** Indonesian natural-language list join — "MMC", "MMC dan TMM", "MMC, AMIN, dan TMM". Private
 *  to this file; only `personCollisionMessage` below needs it. */
function joinNatural(items) {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} dan ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, dan ${items[items.length - 1]}`;
}

/**
 * The explanation text for `PersonCollisionModal` (Stage 3 batch 3a, Part B) — built here,
 * not inside that component, mirroring `DeleteConfirmModal`'s own "caller supplies the
 * specific wording" convention (see that component's header comment). One function covers all
 * three trigger sites since the shape of the sentence is genuinely the same, only the verb
 * changes: `kind` must match whatever `change.kind` was passed to `detectPersonCollision` to
 * produce `result`.
 *
 * `newBranchName` (required for `"add-person"`/`"add-role"`, ignored for `"rename"`): the
 * caller already knows exactly which branch it just proposed — the newly-picked one in the
 * role-fields step — so this is passed directly rather than re-derived from `result.branchIds`
 * (which is the OVERLAP between the two people's role-sets, not necessarily just the one branch
 * being added, once a subset relationship is involved — see `detectPersonCollision`'s own
 * header comment for why subset counts as a match at all).
 */
export function personCollisionMessage(kind, result, branches, newBranchName) {
  const names = result.branchIds.map((id) => branchIdName(branches, id));
  const dan = joinNatural(names);
  const label = `${result.name} — ${names.join(", ")}`;
  if (kind === "add-person") {
    return `Sudah ada ${result.existing.name} di ${dan}. Orang baru bernama "${result.name}" ini juga akan tercatat di ${newBranchName}, sehingga keduanya tampil sebagai "${label}" dan tidak bisa dibedakan saat memilih.`;
  }
  if (kind === "rename") {
    return `${result.existing.name} sudah memegang ${dan}. Mengganti nama jadi "${result.name}" membuat keduanya tampil sebagai "${label}" dan tidak bisa dibedakan saat memilih.`;
  }
  return `${result.existing.name} sudah memegang ${dan}. Menambahkan ${newBranchName} ke ${result.name} ini berarti keduanya tampil sebagai "${label}" dan tidak bisa dibedakan saat memilih.`;
}

/**
 * Suggest a 1-4 char code from a typed name (Phase T1e's inline "+ Tambah sales baru…"
 * quick-add) — mirrors utils/categoryCode.js's suggestCategoryCode exactly (word-initials,
 * then a numeric-suffix fallback on collision), capped at 4 chars instead of 5 since that's
 * this collection's own limit (`persons.js`'s own `CODE_RE`, Stage 4). A blank/unparseable name falls back to "SP"
 * rather than an invalid empty code.
 */
export function suggestSalesCode(name, existingCodes = []) {
  const words = norm(name)
    .toUpperCase()
    .replace(/[.,/()\\&+-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter((w) => w.length > 0);

  const inUse = (code) => existingCodes.some((c) => String(c).toUpperCase() === code);

  const c1 = words.length === 0
    ? "SP"
    : words.length === 1
      ? words[0].slice(0, 2)
      : words.map((w) => w[0]).join("").slice(0, 4);

  if (c1 && !inUse(c1)) return c1;

  for (let n = 2; n < 1000; n++) {
    const suffix = String(n);
    const candidate = (c1 || "SP").slice(0, Math.max(1, 4 - suffix.length)) + suffix;
    if (!inUse(candidate)) return candidate;
  }
  return c1 || "SP"; // exhausted 2-999 — extremely unlikely at this app's scale
}

/**
 * Contact/Sales link-up: resolve which of a Contact's own `personIds` (SALESPERSON PERSON ids,
 * never role ids — see `utils/contacts.js`) hold an ACTIVE role at `branchId` (a real transaction
 * anchor's own resolved location — `null` for Pusat, a real `data.branches` id for that Cabang
 * branch). Never called with `branchId === undefined` — a host that doesn't yet know the target
 * branch (a Cabang anchor with no branch chosen yet) must treat that as its own "nothing to
 * resolve yet" state and skip calling this entirely, rather than this function guessing.
 *
 * Returns exactly one of:
 *  - `{kind:"noPeople"}` — `personIds` is empty, OR every entry resolves to nothing (a
 *    since-deleted person — defense in depth only: `deletePerson` is blocked while any Contact's
 *    `personIds` still links to it, so real data should never hit this, but a contact whose only
 *    person was somehow removed must degrade the same as "no people at all," never crash or
 *    fabricate a person).
 *  - `{kind:"single", role}` — exactly one of the contact's people holds a role here.
 *  - `{kind:"multiple", roles}` — more than one does (a contact linked to several salespeople,
 *    each with their own valid role at this same location).
 *  - `{kind:"noRoleHere", persons}` — at least one personId resolves to a real person, but NONE
 *    of them hold a role here — `persons` is every one of them, in `personIds` order, meant for
 *    `SalespersonPicker`'s own addRoleCandidates-style "belum ada peran di ..." offer (never a
 *    silently different person instead).
 */
export function resolveContactSalespersonRoles(persons, salespeople, personIds, branchId) {
  const ids = Array.isArray(personIds) ? personIds : [];
  const resolvedPersons = ids
    .map((pid) => persons.find((p) => p.id === pid))
    .filter(Boolean);
  if (resolvedPersons.length === 0) return { kind: "noPeople" };

  const roles = [];
  const noRolePersons = [];
  for (const person of resolvedPersons) {
    const role = salespeople.find(
      (s) => s.personId === person.id && s.branchId === branchId && s.active !== false
    );
    if (role) roles.push(role);
    else noRolePersons.push(person);
  }
  if (roles.length === 1) return { kind: "single", role: roles[0] };
  if (roles.length > 1) return { kind: "multiple", roles };
  return { kind: "noRoleHere", persons: noRolePersons };
}
